/* GmailComposerModal — Email composer pro 1-na-1 z karty klienta (v4.15.0).
 *
 * Otevírá se z klient karty „✉️" tlačítka.
 *
 * v2026-08: composer už NENÍ vázaný na Gmail. Odesílá se tou cestou, kterou
 * má poradce připojenou — Gmail, nebo firemní e-mail přes SMTP. Dřív mířil
 * natvrdo na /api/gmail/send, takže poradce s připojeným SMTP viděl místo
 * formuláře hlášku „Gmail není propojen" a nemohl odeslat vůbec nic.
 *
 * Features:
 *  - Subject + body (rich textarea s plain/HTML toggle)
 *  - Pre-fill: To = klientův email, Subject = „Pan/Paní {jméno}"
 *  - Šablony (Po schůzce / Pozvánka / Připomínka výročí / atd.)
 *  - Po odeslání zaznamenáno do history (Historie tab v klient složce)
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Mail, FileText, Loader2, AlertTriangle, ExternalLink, Eye, Code } from "lucide-react";
import { toast } from "sonner";
import { nactiEmailTransport, odesliEmail, zpravaChyby } from "@/lib/emailTransport";
import { osloveniProZpravu } from "@/lib/czechNames";
import { useLanguage } from "@/contexts/LanguageContext";

import { TL } from "@/lib/prekladyUI";
const TEMPLATES = [
  {
    id: "post-meeting",
    label: "Po schůzce — díky",
    subject: "Děkujeme za schůzku",
    body: `Dobrý den {{salutation}} {{surname}},

děkujeme za dnešní schůzku, bylo nám potěšením. Přikládám materiály, o kterých jsme spolu mluvili. V příštích dnech vám připravím konkrétní nabídku.

Kdyby cokoliv, jsem k dispozici telefonicky.

Hezký den,
{{advisor_name}}`,
  },
  {
    id: "intro",
    label: "Pozvánka na první schůzku",
    subject: "Schůzka ohledně finančního plánu",
    body: `Dobrý den {{salutation}} {{surname}},

ozývám se na základě našeho dřívějšího kontaktu ohledně konzultace finančního plánu.

Mohli bychom se sejít na schůzce — buď osobně, nebo online (Google Meet / Teams). Mám volno tento týden ve čtvrtek nebo pátek odpoledne.

Co vám vyhovuje?

S pozdravem,
{{advisor_name}}`,
  },
  {
    id: "fixation",
    label: "Připomínka výročí / fixace",
    subject: "Vaše fixace končí — pojďme se podívat na možnosti",
    body: `Dobrý den {{salutation}} {{surname}},

ozývám se vám ohledně blížícího se konce fixačního období vaší hypotéky. Dle našich dat je výročí už za necelý měsíc — než se zaregistruje automatické pokračování za standardní sazbu, doporučuji projít aktuální možnosti.

Stihl bych se podívat tento nebo příští týden. Co vám vyhovuje?

S pozdravem,
{{advisor_name}}`,
  },
];

const SALUTATION_MAP = {
  "Pan": "pane",
  "Paní": "paní",
  "Vážený pane": "vážený pane",
  "Vážená paní": "vážená paní",
};

/* Po dosazení tokenů uklidí mezery: když oslovení nebo příjmení chybí, nesmí
   zbýt „Dobrý den  ," ani „Dobrý den ,". */
function uklidOsloveni(text) {
  return text.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+([,.!?])/g, "$1");
}

export default function GmailComposerModal({ open, onClose, contact, advisorName = "", onSent }) {
  const { lang } = useLanguage();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bodyMode, setBodyMode] = useState("text"); // text | html
  const [transport, setTransport] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Zjisti, kudy se dnes dá odeslat — Gmail NEBO firemní e-mail (SMTP).
  useEffect(() => {
    if (!open) return;
    setStatusLoading(true);
    nactiEmailTransport()
      .then(setTransport)
      .catch(() => setTransport({ transport: null, gmailNakonfigurovan: true }))
      .finally(() => setStatusLoading(false));
  }, [open]);

  const muzeOdeslat = !!transport?.transport;
  const jeSmtp = transport?.transport === "smtp";

  // Reset form on close
  useEffect(() => {
    if (!open) {
      setSubject("");
      setBody("");
    }
  }, [open]);

  const applyTemplate = (t) => {
    const sal = SALUTATION_MAP[contact?.salutation || ""] || "";
    /* {{surname}} stojí v šablonách za „Dobrý den pane / paní" — tedy OSLOVENÍ,
       které se skloňuje do 5. pádu: Novák → Nováku, Svoboda → Svobodo.
       Přechýlená (Nováková) i přídavná (Novotný) příjmení nechává engine být.
       Když si tvarem není jistý, vrátí prázdno a zůstane jen pozdrav s oslovením
       bez příjmení (Dobrý den pane,). */
    const prijmeni = osloveniProZpravu(contact?.surname, lang);
    const dosad = (s) => uklidOsloveni(
      s.replace(/\{\{salutation\}\}/g, sal)
        .replace(/\{\{name\}\}/g, contact?.name || "")
        .replace(/\{\{surname\}\}/g, prijmeni)
        .replace(/\{\{advisor_name\}\}/g, advisorName)
    );
    setSubject(dosad(t.subject));
    setBody(dosad(t.body));
  };

  const send = async () => {
    if (!contact?.email) {
      toast.error("Klient nemá vyplněný email");
      return;
    }
    if (!subject.trim()) {
      toast.error("Doplň předmět");
      return;
    }
    if (!body.trim() || body.length < 5) {
      toast.error("Doplň tělo emailu");
      return;
    }
    setSending(true);
    try {
      await odesliEmail({
        to: [contact.email],
        subject,
        bodyText: bodyMode === "text" ? body : "",
        bodyHtml: bodyMode === "html" ? body : "",
        contactId: contact.id,
      }, transport);
      toast.success(`Email odeslán na ${contact.email}`);
      onSent && onSent();
      onClose();
    } catch (err) {
      // Detail může přijít i jako pole objektů (FastAPI 422) — nikdy ho
      // nepředávej do JSX bez převodu, shodilo by to celou aplikaci.
      toast.error("Odeslání selhalo: " + zpravaChyby(err));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.45)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          transition={{ type: "spring", bounce: 0.18, duration: 0.4 }}
          onClick={(e) => e.stopPropagation()}
          className="rounded-2xl overflow-hidden w-full max-w-3xl flex flex-col"
          style={{
            background: "var(--bg, #FAF7F3)",
            boxShadow: "0 30px 80px rgba(80,50,30,0.30), 0 8px 24px rgba(80,50,30,0.15)",
            maxHeight: "90vh",
          }}
          data-testid="gmail-composer-modal"
        >
          {/* Header */}
          <div className="px-5 py-3 flex items-center justify-between flex-shrink-0" style={{ borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--accent-tint)", color: "var(--accent)" }}>
                <Mail size={15} />
              </div>
              <div>
                <div className="text-[14px] font-bold tracking-tight" style={{ color: "var(--ink)" }}>
                  Nový email
                </div>
                <div className="text-drobny mono" style={{ color: "var(--ink-muted)" }}>
                  → {contact?.email || "(bez emailu)"} ({contact?.name} {contact?.surname})
                </div>
              </div>
            </div>
            <button aria-label={TL("Zavřít")}
              onClick={onClose}
              data-testid="gmail-composer-close"
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5"
              style={{ color: "var(--ink-muted)" }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {statusLoading ? (
              <div className="flex items-center justify-center py-10 gap-2">
                <Loader2 size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
                <span className="text-sm" style={{ color: "var(--ink-muted)" }}>Načítám…</span>
              </div>
            ) : !muzeOdeslat ? (
              // ─── Není co použít — poctivá hláška + funkční cesta ─────
              <div
                className="rounded-xl p-5 text-center"
                style={{
                  background: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, #FFFFFF 100%)",
                  border: "1px solid rgba(245,158,11,0.35)",
                }}
                data-testid="composer-no-transport"
              >
                <AlertTriangle size={28} className="mx-auto mb-2" style={{ color: "#92400E" }} />
                <div className="text-[14px] font-bold mb-1" style={{ color: "var(--ink)" }}>
                  E-mail zatím není propojený
                </div>
                <div className="text-[12px] mb-4" style={{ color: "var(--ink-muted)" }}>
                  {transport?.gmailNakonfigurovan === false ? (
                    <>
                      Gmail zatím správce nezapnul, takže ten připojit nejde.
                      <br /><b>Firemní e-mail (SMTP) v Nastavení ale funguje hned</b> — stačí adresa a heslo,
                      žádné čekání na Google.
                    </>
                  ) : (
                    <>
                      Než začneš psát klientům z aplikace, připoj si v Nastavení svůj e-mail.
                      <br /><b>Firemní e-mail (SMTP) funguje hned</b> s jakoukoli adresou.
                    </>
                  )}
                </div>
                <a
                  href="/settings"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-bold transition-all hover:translate-y-[-1px]"
                  style={{
                    background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                    color: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(194,89,52,0.30)",
                  }}
                  data-testid="gmail-composer-goto-settings"
                >
                  <ExternalLink size={11} /> Otevřít Nastavení
                </a>
              </div>
            ) : (
              <>
                {/* Odesilatel — kterou cestou to půjde */}
                <div className="text-drobny mb-3 px-2 py-1.5 rounded inline-flex items-center gap-1.5"
                  style={{ background: "rgba(34,134,90,0.10)", color: "#22865A" }}
                  data-testid="composer-sender"
                >
                  <Mail size={10} /> Odesilatel: <span className="font-bold mono">{transport.email}</span>
                  <span className="opacity-70">({jeSmtp ? "firemní e-mail" : "Gmail"})</span>
                </div>

                {/* Templates */}
                <div className="mb-3">
                  <div className="text-popisek uppercase tracking-wider font-bold mb-1.5" style={{ color: "var(--ink-muted)", letterSpacing: "0.05em" }}>
                    Šablony
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => applyTemplate(t)}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-drobny font-medium transition-all"
                        style={{ background: "var(--surface)", color: "var(--accent)", border: "1px solid var(--accent)" }}
                        data-testid={`composer-template-${t.id}`}
                      >
                        <FileText size={10} /> {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div className="mb-3">
                  <label className="text-popisek uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--ink-muted)", letterSpacing: "0.05em" }}>
                    Předmět
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Předmět emailu"
                    data-testid="composer-subject"
                    className="w-full rounded-md px-3 py-2 text-sm outline-none"
                    style={{ background: "var(--surface)", border: "1px solid #D4C5B0", color: "var(--ink)" }}
                  />
                </div>

                {/* Body mode toggle */}
                <div className="flex items-center gap-2 mb-1.5">
                  <label className="text-popisek uppercase tracking-wider font-bold" style={{ color: "var(--ink-muted)", letterSpacing: "0.05em" }}>
                    Tělo emailu
                  </label>
                  <div className="ml-auto flex gap-1">
                    <button
                      onClick={() => setBodyMode("text")}
                      className="text-drobny px-2 py-0.5 rounded transition-colors"
                      style={bodyMode === "text"
                        ? { background: "var(--accent)", color: "var(--on-accent)" }
                        : { background: "var(--surface)", color: "var(--ink-muted)", border: "1px solid var(--border)" }}
                    >
                      <FileText size={9} className="inline mr-0.5" /> Plain
                    </button>
                    <button
                      onClick={() => setBodyMode("html")}
                      className="text-drobny px-2 py-0.5 rounded transition-colors"
                      style={bodyMode === "html"
                        ? { background: "var(--accent)", color: "var(--on-accent)" }
                        : { background: "var(--surface)", color: "var(--ink-muted)", border: "1px solid var(--border)" }}
                    >
                      <Code size={9} className="inline mr-0.5" /> HTML
                    </button>
                  </div>
                </div>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={bodyMode === "html" ? "<p>Dobrý den…</p>" : "Dobrý den…"}
                  data-testid="composer-body"
                  rows={12}
                  className="w-full rounded-md px-3 py-2 text-[13px] outline-none"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid #D4C5B0",
                    color: "var(--ink)",
                    fontFamily: bodyMode === "html" ? "ui-monospace, monospace" : "system-ui, sans-serif",
                  }}
                />
                <div className="text-drobny mt-1 flex justify-between" style={{ color: "var(--ink-faint)" }}>
                  <span>{bodyMode === "html" ? "HTML — můžeš použít tagy" : "Plain text"}</span>
                  <span>{body.length} znaků</span>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          {muzeOdeslat && (
            <div className="px-5 py-3 flex items-center justify-between flex-shrink-0" style={{ borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
              <div className="text-drobny" style={{ color: "var(--ink-muted)" }}>
                {jeSmtp
                  ? (transport?.smtp?.imap_ready
                      ? `Odešle se z ${transport.email} a kopie se uloží do tvé složky Odeslané.`
                      : `Odešle se z ${transport.email}. Kopii do složky Odeslané uložíme, až doplníš IMAP server v Nastavení.`)
                  : `Odešle se z ${transport.email} a uloží do tvé Sent složky v Gmailu.`}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 rounded-md text-[12px] font-medium"
                  style={{ background: "var(--surface)", color: "var(--ink-muted)", border: "1px solid var(--border)" }}
                >
                  Zrušit
                </button>
                <button
                  onClick={send}
                  disabled={sending}
                  data-testid="composer-send"
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[12px] font-bold transition-all hover:translate-y-[-1px]"
                  style={{
                    background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                    color: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(194,89,52,0.30)",
                    opacity: sending ? 0.6 : 1,
                  }}
                >
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Odeslat email
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
